import { useEffect, useRef, useState } from 'preact/hooks'
import { rigPose } from '../../game/rigPose'
import type { RunnerSlot } from './roster'
import { runnerName } from './roster'
import { assetUrl, pieceStyle, placeRig, previewMode, rigImages } from './previewLayout'
import type { RigLayout, RigSizes } from './previewLayout'
import styles from './runnerPreview.module.css'

/** The idle pose at rest; the 2 s breathing loop is CSS (`runnerPreview.module.css`). */
const IDLE = rigPose('idle', 0)
/** Head room above the rig, as a share of its height. */
const TOP_ROOM = 1.04

function loadSize(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    if (typeof Image === 'undefined') {
      reject(new Error('no Image'))
      return
    }
    const img = new Image()
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight })
    img.onerror = () => reject(new Error(`missing ${src}`))
    img.src = assetUrl(src)
  })
}

/** Natural sizes of the torso and leg once every rig image has loaded; null while loading. */
function useRigSizes(slot: RunnerSlot): { sizes: RigSizes | null; failed: boolean } {
  const [state, setState] = useState<{ kind: string; sizes: RigSizes | null; failed: boolean }>({
    kind: '',
    sizes: null,
    failed: false,
  })
  useEffect(() => {
    const rig = slot.rig
    if (!rig) return
    let live = true
    Promise.all(rigImages(rig).map(loadSize))
      .then(([, torso, , leg]) => {
        if (live)
          setState({ kind: slot.kind, sizes: { torsoW: torso!.w, legH: leg!.h }, failed: false })
      })
      .catch(() => {
        if (live) setState({ kind: slot.kind, sizes: null, failed: true })
      })
    return () => {
      live = false
    }
  }, [slot])
  return state.kind === slot.kind ? state : { sizes: null, failed: false }
}

/** Host height in CSS px, followed with a ResizeObserver. */
function useHeight(): [{ current: HTMLDivElement | null }, number] {
  const ref = useRef<HTMLDivElement>(null)
  const [h, setH] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    setH(el.clientHeight)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setH(el.clientHeight))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, h]
}

function Rig({ layout, height }: { layout: RigLayout; height: number }) {
  const scale = height / (-layout.headTop * TOP_ROOM)
  return (
    <div class={styles.rig} style={{ transform: `scale(${scale.toFixed(4)})` }}>
      <div class={styles.breathe}>
        {layout.pieces.map((p) => (
          <img
            key={p.key}
            class={`${styles.piece} ${p.back ? styles.back : ''} ${p.key.startsWith('arm') ? styles.arm : ''}`}
            src={assetUrl(p.src)}
            alt=""
            draggable={false}
            style={pieceStyle(p)}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * A runner drawn with DOM images (PHASE3 T3.1, D-048): the cutout rig in the idle pose with a
 * breathing loop, a single delivered image, or the coloured placeholder box with the name. It
 * fills its parent's height, feet on the parent's bottom edge.
 */
export function RunnerPreview({ slot, class: cls = '' }: { slot: RunnerSlot; class?: string }) {
  const { sizes, failed } = useRigSizes(slot)
  const [ref, height] = useHeight()
  const mode = previewMode(slot, failed)
  let body = null
  if (mode === 'rig' && slot.rig && sizes && height > 0) {
    body = <Rig layout={placeRig(slot.rig, IDLE, sizes)} height={height} />
  } else if (mode === 'image' && slot.image) {
    body = <img class={styles.single} src={assetUrl(slot.image)} alt="" draggable={false} />
  } else if (mode === 'box') {
    body = (
      <div class={styles.box} style={{ background: slot.color }}>
        <span>{runnerName(slot)}</span>
      </div>
    )
  }
  return (
    <div ref={ref} class={`${styles.host} ${cls}`} role="img" aria-label={runnerName(slot)}>
      <div key={slot.kind} class={styles.fade}>
        {body}
      </div>
    </div>
  )
}
